<template>
    <modal v-bind="$attrs" v-on="$listeners">
      <form @submit.prevent="submitForm">
        <div class="d-flex align-items-center flex-column">
          <div class="col-12">
            <fg-input :label="$t('generic.product')"
                      :error="getError($t('generic.product'))"
                      required>
              <product-select :options="jobUpgradeProducts"
                              :name="$t('generic.product')"
                              v-model="selectedProduct"
                              @change="productChange"
                              v-validate="'required|not_empty_object'">
              </product-select>
            </fg-input>
          </div>
          <div class="col-12">
            <fg-input :label="$t('generic.jobBoard')">
              <el-select v-model="selectedJobBoard" class="select-default">
                  <el-option v-for="board in currentJob.job_boards"
                             :key="board.id"
                             :label="board.name"
                             :value="board.id"
                             class="select-default">
                  </el-option>
              </el-select>
            </fg-input>
          </div>
          <div class="col-12">
            <fg-input :label="$t('generic.paymentType')" required
                      :error="getError($t('generic.paymentType'))">
              <payment-type-select :disabled="!productSelected"
                                   :payment-type-filter="paymentTypeFilter"
                                   :credits-amount="totalPriceCredits"
                                   :credits-balance="creditsBalance"
                                   :name="$t('generic.paymentType')"
                                   v-model="selectedPayment"
                                   v-validate="'required|not_empty_object'">
              </payment-type-select>
            </fg-input>
          </div>
          <div class="col-12">
            <fg-input :placeholder="$t('generic.price')"
                      :name="$t('generic.price')"
                      :error="getError($t('generic.price'))"
                      :label="`${$t('generic.price')} (${currencySymbol})`"
                      v-validate="'required|min_value:0|numeric|'"
                      type="number"
                      v-model="price">
            </fg-input>
          </div>
          <div class="col-12">
            <fg-input :label="$t('generic.po_number')"
                      :name="$t('generic.po_number')"
                      required
                      v-model="po_number">
            </fg-input>
          </div>
          <template v-if="selectedAddress">
            <div class="col-12">
              <section-separator has-border size="lg">
                {{$t('generic.address')}}
              </section-separator>
            </div>
            <div class="col-12">
              <fg-input :placeholder="$t('client.management.edit.address')"
                        :label="$t('client.management.edit.address')"
                        id="city-locality"
                        :enable-geolocation="true"
                        component="vue-google-autocomplete"
                        name="job city"
                        v-model="selectedAddress.address_line_1"
                        @placechanged="(data, place) => onAddressChange(data, place)"
                        addon-left-icon="nc-icon nc-pin-3">
              </fg-input>
            </div>
            <div class="col-12">
              <fg-input :name="$t('client.management.edit.postal_code')"
                        :label="$t('client.management.edit.postal_code')"
                        :placeholder="$t('client.management.edit.postal_code')"
                        v-model="selectedAddress.postal_code">
              </fg-input>
            </div>
            <div class="col-12">
              <fg-input :name="$t('generic.city')"
                        :placeholder="$t('generic.city')"
                        :label="$t('generic.city')"
                        v-model="selectedAddress.city">
              </fg-input>
            </div>
            <div class="col-12">
              <fg-input :label="$t('generic.country')">
                <country-autocomplete
                  :name="$t('generic.country')"
                  :placeholder="$t('generic.country')"
                  v-model="selectedAddress.country_code">
                </country-autocomplete>
              </fg-input>
            </div>
          </template>
          <div class="col-12">
            <submit-button block :loading="loading" native-type="submit"></submit-button>
          </div>
        </div>
      </form>
    </modal>
</template>
<script>
  import get from 'lodash/get'
  import Vue from 'vue'
  import { Select, Option } from 'element-ui'
  import ProductSelect from 'src/components/Autocomplete/ProductSelect'
  import PaymentTypeSelect from 'src/components/Autocomplete/PaymentTypeSelect'
  import ClientService from 'src/services/ClientService'
  import JobService from 'src/services/JobService'
  import { findCountryCode } from 'src/utils/googleAutocompleteUtils';
  import CountryAutocomplete from 'src/components/Autocomplete/CountryAutocomplete';
  import VueGoogleAutocomplete from 'src/components/Autocomplete/VueGoogleAutocomplete'

  Vue.component(VueGoogleAutocomplete.name, VueGoogleAutocomplete);
  import { mapGetters, mapState } from 'vuex'
  import { paymentTypes, CREDIT, INVOICE } from 'src/config/paymentTypes'
  export default {
    inheritAttrs: false,
    components: {
      CountryAutocomplete,
      ProductSelect,
      PaymentTypeSelect,
      [Select.name]: Select,
      [Option.name]: Option,
    },
    data () {
      return {
        selectedProduct: {},
        selectedPayment: {},
        selectedJobBoard: null,
        creditsBalance: null,
        client: {},
        po_number: '',
        price: 0,
        selectedAddress: null,
        loading: false
      }
    },
    computed: {
      ...mapGetters('config', ['jobUpgradeProducts']),
      ...mapState({
        currentJob: state => state.jobs.currentJob,
        currencySymbol: state => state.config.currencySymbol
      }),
      productSelected() {
        return Object.keys(this.selectedProduct).length > 0
      },
      totalPriceCredits() {
        return this.selectedProduct.credits
      }
    },
    methods: {
      onAddressChange({ postal_code, country, locality, administrative_area_level_1, route, street_number, longitude, latitude }, place) {
        let address = {
          ...this.selectedAddress
        };
        if (postal_code) {
          address.postal_code = postal_code;
        }
        if (country) {
          address.country = country;
          address.country_code = findCountryCode(place);
        }
        if (locality) {
          address.city = locality;
        }
        if (administrative_area_level_1) {
          address.state_of_province = administrative_area_level_1;
        }
        if (route) {
          address.address_line_1 = `${route} ${street_number}`
        }
        this.selectedAddress = address
      },
      paymentTypeFilter(payments) {
        return payments.filter(paymentType => {
          return [paymentTypes[CREDIT], paymentTypes[INVOICE]].includes(paymentType.type)
        })
      },
      trySetJobBoard() {
        let jobBoards = get(this.currentJob, 'job_boards', [])
        if (jobBoards.length === 1) {
          this.selectedJobBoard = jobBoards[0]
        }
      },
      async productChange(product) {
        this.price = product.price
        await this.getCreditsBalance()
      },
      async getCreditsBalance () {
        if (!this.currentJob) {
          return
        }
        let clientId = this.currentJob.client_uuid
        let {data} = await ClientService.getClientCredits(clientId)
        this.creditsBalance = data.credits
      },
      async getClient() {
        let clientId = this.currentJob.client_uuid
        let {data} = await ClientService.getById(clientId)
        this.client = data
        let addresses = get(this.client, 'addresses', [])
        this.selectedAddress = addresses.find(address => address.financial)
      },
      async submitForm() {
        let valid = await this.$validator.validateAll()
        if (!valid) {
          return
        }
        try {
          this.loading = true
          let model = {
            product_id: this.selectedProduct.product_id,
            job_board_id: this.selectedJobBoard.id,
            po_number: this.po_number,
            price: this.price,
            payment_type_id: this.selectedPayment.id
          }
          await ClientService.updateFinancialAddress(this.client, this.selectedAddress)
          await JobService.upSell(this.currentJob.uuid, model)
          this.$emit('update:show', false)
        } catch(e) {
          console.warn(e)
        }
        this.loading = false
      }
    },
    async created() {
      this.trySetJobBoard()
      await Promise.all([
        this.getCreditsBalance(),
        this.getClient()
      ])
    }
  }
</script>
<style>
</style>
